import api from "./http";

const API_URL = `/api/email-monitored-jobs`;

/**
 * Récupère la liste des jobs de sauvegarde avec filtres optionnels
 */
export async function getMonitoredJobs({
    clientId,
    limit = 50,
    offset = 0,
} = {}) {
    try {
        const params = new URLSearchParams();
        if (limit) params.append("limit", limit);
        if (offset) params.append("offset", offset);
        if (clientId) params.append("clientId", clientId);

        const res = await api.get(`${API_URL}?${params.toString()}`, {
            timeout: 15000,
        });

        // Gérer différents formats de réponse
        if (res.data?.data) return res.data;
        if (res.data?.success && Array.isArray(res.data.data)) return res.data;
        if (Array.isArray(res.data)) {
            return { data: res.data, total: res.data.length, success: true };
        }

        return { data: [], total: 0, success: true };
    } catch (error) {
        console.error("Error fetching monitored jobs:", error);
        return { data: [], total: 0, success: false, error };
    }
}

/**
 * Récupère un job de sauvegarde par son ID
 */
export async function getMonitoredJob(id) {
    const res = await api.get(`${API_URL}/${id}`);
    return res.data?.data || res.data;
}

/**
 * Crée un nouveau job de sauvegarde
 */
export async function createMonitoredJob(data) {
    const res = await api.post(API_URL, data);
    return res.data?.data || res.data;
}

/**
 * Met à jour un job de sauvegarde
 */
export async function updateMonitoredJob(id, data) {
    const res = await api.put(`${API_URL}/${id}`, data);
    return res.data?.data || res.data;
}

/**
 * Supprime un job de sauvegarde
 */
export async function deleteMonitoredJob(id) {
    await api.delete(`${API_URL}/${id}`);
}

/**
 * Marque manuellement un job de sauvegarde comme corrigé
 */
export async function markMonitoredJobCorrected(id) {
    const res = await api.post(`${API_URL}/${id}/mark-corrected`);
    return res.data;
}

/**
 * Récupère la liste des clients pour les filtres
 */
export async function getClientsForFilter() {
    try {
        let allClients = [];
        let url = "/api/clients?itemsPerPage=1000&pagination=false&page=1";

        while (url) {
            const res = await api.get(url, {
                headers: { Accept: "application/ld+json" },
                timeout: 15000,
            });
            const data = res.data;
            if (data && Array.isArray(data["hydra:member"])) {
                allClients = allClients.concat(data["hydra:member"]);
            } else if (data && Array.isArray(data["member"])) {
                allClients = allClients.concat(data["member"]);
            } else if (Array.isArray(data)) {
                allClients = allClients.concat(data);
            }

            if (data?.view?.next) {
                url = data.view.next;
            } else if (data?.["hydra:view"]?.["hydra:next"]) {
                url = data["hydra:view"]["hydra:next"];
            } else {
                url = null;
            }
        }

        return allClients;
    } catch (error) {
        console.error("Error fetching clients:", error);
        return [];
    }
}

/**
 * Récupère tous les rapports d'analyse (historique complet, toutes syncs)
 */
export async function getAllReports({
    jobId,
    clientId,
    statusTypeName,
    search,
    dateFrom,
    dateTo,
    excludeToday = true,
    limit = 25,
    offset = 0,
} = {}) {
    try {
        const params = new URLSearchParams();
        params.append("limit", limit);
        params.append("offset", offset);
        if (jobId) params.append("jobId", jobId);
        if (clientId) params.append("clientId", clientId);
        if (statusTypeName) params.append("statusTypeName", statusTypeName);
        if (search) params.append("search", search);
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);
        if (excludeToday) params.append("excludeToday", "1");

        const res = await api.get(
            `/api/email-report-statuses?${params.toString()}`,
            { timeout: 15000 },
        );
        return res.data;
    } catch (error) {
        console.error("Error fetching reports:", error);
        return { data: [], total: 0, success: false, error };
    }
}
export async function syncAndAnalyzeEmails(options = {}) {
    try {
        const res = await api.post(
            "/api/email-sync-analyze",
            {
                daysBack: options.daysBack ?? 7,
                statusTypeName: options.statusTypeName || "COMPLETED",
                emailLimit: options.emailLimit || 1000,
                unreadOnly: options.unreadOnly || false,
                jobIds: options.jobIds || null,
            },
            {
                timeout: 45000,
            },
        );
        return res.data;
    } catch (error) {
        console.error("Error syncing and analyzing emails:", error);
        throw error;
    }
}

export async function getReportEmails(reportId) {
    const res = await api.get(`/api/email-report-statuses/${reportId}/emails`);
    return res.data?.data || [];
}

export async function detachReportEmail(reportId, emailId) {
    const res = await api.post(`/api/email-report-statuses/${reportId}/emails/${emailId}/detach`);
    return res.data;
}

export async function transferReportEmail(reportId, emailId, targetReportId) {
    const res = await api.post(`/api/email-report-statuses/${reportId}/emails/${emailId}/transfer`, {
        targetReportId,
    });
    return res.data;
}
